import {
  Col,
  Flex,
  Form,
  Input,
  Paragraph,
  Row,
  Switch,
  type FormInstance,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { getInputValue } from "@src/presentation/pages/Organizations/utils/addEditFormUtils";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  createDeductionCategoryKeyRules,
  generateDeductionCategoryKeyFromName,
} from "./deductionCategoryKeyValidation";

export interface DeductionCategoryAddEditFormValues {
  categoryName: string;
  categoryKey: string;
  isActive: boolean;
}

interface MasterDataDeductionCategoryAddEditFormProps {
  form: FormInstance<DeductionCategoryAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: DeductionCategoryAddEditFormValues;
  isLinked?: boolean;
  onSubmit: (values: DeductionCategoryAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const MasterDataDeductionCategoryAddEditForm = ({
  form,
  mode,
  initialValues,
  isLinked = false,
  onSubmit,
  onFormValidityChange,
}: MasterDataDeductionCategoryAddEditFormProps) => {
  const isKeyEditedManuallyRef = useRef(false);
  const categoryKeyDisabled = useMemo(() => mode === "edit", [mode]);
  const statusDisabled =
    mode === "edit" && Boolean(isLinked) && Boolean(initialValues.isActive);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const categoryName = values.categoryName?.trim() ?? "";
    const categoryKey = values.categoryKey?.trim() ?? "";

    const hasRequiredValues =
      categoryName !== "" && (mode === "edit" || categoryKey !== "");

    const isDirty =
      mode === "create"
        ? true
        : categoryName !== (initialValues.categoryName?.trim() ?? "") ||
          Boolean(values.isActive) !== Boolean(initialValues.isActive);

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    isKeyEditedManuallyRef.current = mode === "edit";
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, mode, updateFormValidity]);

  const nameRules = useMemo(
    () => [
      { required: true, message: "Category name is required" },
      {
        max: 255,
        message: "Category name must be less than or equal to 255 characters.",
      },
    ],
    [],
  );

  const categoryKeyRules = useMemo(() => createDeductionCategoryKeyRules(), []);

  const syncCategoryKeyFromName = (valueOrEvent: unknown) => {
    if (categoryKeyDisabled || isKeyEditedManuallyRef.current) return;
    form.setFieldsValue({
      categoryKey: generateDeductionCategoryKeyFromName(
        getInputValue(valueOrEvent),
      ),
    });
    form.validateFields(["categoryKey"]).catch(() => {});
  };

  const handleCategoryNameChange = (valueOrEvent: unknown) => {
    form.setFieldsValue({
      categoryName: getInputValue(valueOrEvent),
    });
    syncCategoryKeyFromName(valueOrEvent);
    form.validateFields(["categoryName"]).catch(() => {});
    updateFormValidity();
  };

  const handleCategoryKeyChange = () => {
    if (categoryKeyDisabled) return;
    isKeyEditedManuallyRef.current = true;
    form.validateFields(["categoryKey"]).catch(() => {});
    updateFormValidity();
  };

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="master-data-deduction-category-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item
              name="categoryName"
              label="Category Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={nameRules}
            >
              <Input
                placeholder="Category name"
                onChange={handleCategoryNameChange}
                onInput={handleCategoryNameChange}
                onKeyUp={handleCategoryNameChange}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="categoryKey"
              label="Category Key"
              validateTrigger={["onChange", "onBlur"]}
              rules={categoryKeyDisabled ? undefined : categoryKeyRules}
              helpText={
                categoryKeyDisabled
                  ? "Once category key is created, it cannot be changed."
                  : undefined
              }
            >
              <Input
                placeholder="CATEGORY-KEY"
                isDisabled={categoryKeyDisabled}
                onChange={handleCategoryKeyChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={2}>
            <Flex
              align="start"
              gap={1}
              className="status-switch"
              direction="column"
            >
              <Paragraph className="m-0 custom-label">Status</Paragraph>
              <Form.Item name="isActive" valuePropName="isSelected" noStyle>
                <Switch
                  className="p-0 mt-1"
                  size="small"
                  isDisabled={statusDisabled}
                  aria-label="Category active status"
                />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
